import type { ReactNode } from 'react';
import { DispatchMap } from '../map/DispatchMap';
import { Button } from '../ui/Button';
import { UnplacedList } from './UnplacedList';
import type { dayPins } from './boardPins';

/**
 * The tri-pane's GEOMETRY (113 decision 1), as its own component.
 *
 * ⭐ At ≥1280 the map takes a fixed 360px column beside the board; below that it drops
 * UNDERNEATH. Squeezing an hour grid and a map into the same 1024px row leaves neither
 * usable — 11 hours at any legible column width is already a 1200px scroller.
 *
 * ⚠️ `minmax(0, 1fr)` and `min-w-0`, not `1fr` and nothing. A grid track's default minimum
 * is `auto`, so a board wide enough to scroll would push the TRACK wider instead of
 * scrolling inside itself, and the page body would scroll sideways — the one rule A-1 makes
 * non-negotiable. This is also why the board is handed in as a child rather than being
 * wrapped by the caller: the `min-w-0` belongs to the grid that creates the problem.
 */
export function DispatchPanes({ board, map }: { board: ReactNode; map: ReactNode }) {
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="min-w-0">{board}</div>
      {map}
    </div>
  );
}

/**
 * The map pane — the second of the tri-pane's three (113 decision 9).
 *
 * 🟢 A REUSE: `DispatchMap` is 112's shipped Leaflet map, keyless and unbilled. It grew one
 * prop for this screen (`selectedId`) and nothing else.
 *
 * ⚠️ **Collapsible, and the toggle is at the top.** Below 1280 the map sits between the
 * board and the queue list, so a dispatcher who is not using it today would otherwise
 * scroll past 400px of tiles on every glance at the bottom pane.
 *
 * ⚠️ **The stops it cannot draw are NAMED, not counted away.** A visit with no coordinates
 * is still an hour of somebody's day; the pane lists each one with the reason and the number
 * its pin would have carried, so the gap in the numbering on the map has an answer directly
 * underneath it.
 */
export function MapPane({
  open,
  onToggle,
  pins,
  unplaced,
  selectedId,
  onSelect,
}: {
  open: boolean;
  onToggle: () => void;
  pins: ReturnType<typeof dayPins>['pins'];
  unplaced: ReturnType<typeof dayPins>['unplaced'];
  selectedId: string | null;
  onSelect: (visitId: string) => void;
}) {
  return (
    <section className="min-w-0 space-y-2 rounded-[var(--radius)] border border-border bg-surface p-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-heading text-sm font-semibold">
          Map{' '}
          <span className="font-normal text-muted-foreground">
            · {pins.length} {pins.length === 1 ? 'stop' : 'stops'}
          </span>
        </h2>
        <Button variant="ghost" onClick={onToggle} aria-expanded={open}>
          {open ? 'Hide' : 'Show'}
        </Button>
      </div>

      {open ? (
        <>
          {pins.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nothing to draw — no stop today has a located address.
            </p>
          ) : (
            <DispatchMap
              pins={pins}
              selectedId={selectedId}
              onSelect={onSelect}
              className="h-[320px] w-full rounded-[var(--radius-sm)] border border-border xl:h-[440px]"
            />
          )}
          <p className="text-[11px] text-muted-foreground">
            {/* ⚠️ Says what the numbers MEAN, because a reader will otherwise assume they
                are a route. They are the shop's whole day in time order, across every rig,
                and no line joins them — nothing here claims a driving path. */}
            Numbered in time order across every rig. Click a pin to open the job.
          </p>
          {unplaced.length > 0 ? (
            <div className="border-t border-border pt-2">
              {/* The same rows the crew map lists, and like a pin, a row opens the stop. */}
              <UnplacedList
                items={unplaced}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
